import { useEffect, useRef, useCallback } from 'react';
import { useDraftStore } from '../stores/draft';

interface AutoSaveData {
  title: string;
  titleZh?: string;
  content: string;
  contentZh?: string;
  tags: string;
  visibility: 'public' | 'private' | 'unlisted';
  slug: string;
}

export function useAutoSave(articleId: string, data: AutoSaveData, enabled = true) {
  const saveDraft = useDraftStore((state) => state.saveDraft);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string>('');

  const save = useCallback(() => {
    const dataString = JSON.stringify(data);
    if (dataString !== lastSavedRef.current) {
      saveDraft(articleId, data);
      lastSavedRef.current = dataString;
    }
  }, [articleId, data, saveDraft]);

  useEffect(() => {
    if (!enabled) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce save by 1 second
    timeoutRef.current = setTimeout(save, 1000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, enabled, save]);

  // Save immediately on unmount
  useEffect(() => {
    return () => {
      if (enabled) {
        save();
      }
    };
  }, [enabled, save]);

  return { saveNow: save };
}

export function useLoadDraft(articleId: string) {
  const getDraft = useDraftStore((state) => state.getDraft);
  return getDraft(articleId);
}

export function useClearDraft(articleId: string) {
  const deleteDraft = useDraftStore((state) => state.deleteDraft);
  return () => deleteDraft(articleId);
}
